import { get, saveFile } from '@/api/client';
import {
  type ApiShift,
  type ApiUser,
  useAbsenceTypes,
  useCompanyTimeBank,
  usePayrollTypes,
  useRateRules,
  useReport,
  useShifts,
  useUsers,
} from '@/api/data';
import { useLabour, useSales } from '@/api/labour';
import { useProjectRequired } from '@/api/projects';
import { useCan } from '@/auth/caps';
/**
 * Timer til lønn (0017 P0.3; manager redesign 2026-10-09): the month's approved hours per pay type
 * from Engine A (GET /admin/report), the checks before the file leaves, the month's lock and log,
 * and the files. Both CSV files are Engine A's own, the Tripletex one by the same route with
 * format=tripletex (TF-WEB-COMPLETION B). The layout is the approved prototype's: a readiness strip
 * whose checks open beside the list, one month card with the active company's lock in it, a table
 * of people whose pay lines open beside it, and the export in its own panel.
 */
import { T } from '@/i18n';
import { breakPolicy, oslo, tenantRules, today } from '@/rules';
import { costOf, ratesOf } from '@/rules/labour';
import { useTenant } from '@/shell/tenant';
import { Button, Chip, Skeleton, toast } from '@/ui';
import { PeriodControl } from '@/ui/PeriodControl';
import { PersonLabel } from '@/ui/PersonLabel';
import { fmtD, fullName, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { DetailPanel, Explain, Fact, Notice, Status, TabPanel } from '@/ui/page';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { monthLabel, salesState, shiftMonth } from './Omsetning';
import { LonnHead } from './lonn/Head';
import {
  ChangedPeople,
  Delivery,
  MonthHistory,
  MonthLock,
  SYSTEM_NAME,
  invalidatePayMonth,
  usePayMonth,
  when,
} from './lonn/Month';
import { PayDetail, kr } from './lonn/PayDetail';
import { PowerOfficeSend } from './lonn/PowerOffice';
import { SavedReports } from './lonn/Saved';
import { PeoplePick, StoresPick, useScope } from './lonn/Scope';
import { TripletexSend } from './lonn/Tripletex';
import { ROW_STATES, type RowState, checksFor, filterRows, pageOf, rowState, uidOf } from './lonn/ledger';
import { type PayLine, payLines } from './lonn/lines';
import { POWEROFFICE, TRIPLETEX } from './lonn/payrollSystem';
import {
  type Check,
  type CheckRecord,
  type PayrollSystem,
  blockers,
  checks,
  readiness,
  settled,
  steps,
} from './lonn/run';
import { useConnected } from './lonn/useConnected';
import { useApproveMany } from './timeliste/approve';
import { kindOf } from './timeliste/close';
import { OptionRow } from './vaktplan/planning/OptionRow';

/** The D17 notice on this page runs for a month from the change: to the end of 22 October 2026, Oslo. */
export const D17_NOTICE_UNTIL = Date.parse('2026-10-23T00:00:00+02:00');

const monthOf = (d: string) => d.slice(0, 7);
/** Checks that hold «Send» until looked at (#217): a wrong-day clock time or a double clock-in becomes overtime. */
const HOLD_UNTIL_SEEN = ['long-shift', 'overtime-split-day'];
const firstOf = (ym: string) => `${ym}-01`;
const lastOf = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return `${ym}-${String(new Date(Date.UTC(y ?? 2026, m ?? 1, 0)).getUTCDate()).padStart(2, '0')}`;
};

/** «Beregning» on the statement: how the approved amount is made, the pay policy as the report applies it. */
export const CALC = [
  'Bare godkjente dager med stempling inn og ut teller. Planlagte timer betales aldri.',
  'Tillegg per time etter satsreglene under Innstillinger → Lønn: kveld, natt, helg.',
  'Alle godkjente timer står på grunnlønn. Tillegg og overtid står som tillegget alene, slik filen viser dem.',
  'Overtid: timer over 9 på en dag eller over terskelen i en uke, mandag til søndag. En uke som begynner i forrige måned telles fra mandagen, men bare timene i perioden betales.',
  'Godkjente timer er stemplet tid på godkjente vakter etter bedriftens pause, som i filen.',
  'Pause som er stemplet etter at vakten skulle vært slutt, og personen jobber mer enn to timer over, trekkes ikke i inntil en halv time. Den halvtimen er arbeidstid og betales som overtid (arbeidsmiljøloven § 10-9 andre ledd).',
  'Innvilget fravær med lønn regnes med timelønnen og er med i Sum og i begge filene.',
  'Arbeidsgiver betaler bare sine dager ved sykefravær, 16 kalenderdager per arbeidsgiverperiode, og permittering, 15 arbeidsdager. Dagene etter står på en egen linje «(NAV)» til 0 kr. Sykt barn betales for alle dager; dagene etter de 10 første i året står merket «refusjon fra NAV», og bedriften krever dem tilbake fra NAV.',
  'Sykefravær og sykt barn: dagene personen skulle ha jobbet etter planen, med dagsats lik lønnen i Timefly de tre siste kalendermånedene (uten overtid) delt på dagene de jobbet. Arbeidsgiver betaler først etter fire ukers ansettelse (Ansatt fra). Uten vakter i planen betales bare etter Navs regel for tilkallingsvikarer; ellers ingen dager, og Kontroller sier hvorfor.',
  '1. og 17. mai: timene den dagen får søndagstillegget bedriften har, ellers minst 50 % (lov om 1. og 17. mai § 3). Er personen satt opp uten å jobbe, betales de planlagte timene. Fastlønn dekkes av lønnen.',
  'Fastlønn: filen har ingen grunnlønnslinje, fordi lønnssystemet betaler fastlønnen. Tillegg regnes av timesatsen fra månedslønnen og avtalt uke, overtid med timesatsen pluss tillegget, og betalt fravær står uten beløp (sykefravær i dager, annet fravær i timer).',
];

type Pane =
  | { k: 'checks' }
  | { k: 'lines'; id: number }
  | { k: 'export' }
  | { k: 'history' }
  | { k: 'saved' }
  | { k: 'stores' }
  | { k: 'people' }
  | null;

/**
 * One thing standing in the way, with the count, what it costs, and where it is fixed. `acts`
 * replaces the plain Åpne link where the step can do the work itself.
 */
export function CheckRow({
  c,
  note,
  acts,
  link = (to) => to,
}: { c: Check; note?: string; acts?: ReactNode; link?: (to: string) => string }) {
  const recs = c.records ?? [];
  const label = (r: CheckRecord) =>
    `${r.who}${r.date ? `, ${fmtD(r.date)}` : ''}${r.what ? `: ${r.what}` : ''}`;
  const rec = (r: CheckRecord) => (
    <li key={r.key}>
      {r.to ? (
        <Link className="mlink" to={link(r.to)}>
          {label(r)}
        </Link>
      ) : (
        label(r)
      )}
    </li>
  );
  // V6 (#262): who on the left, what is wrong in the middle, the way to fix it on the right
  const one = recs.length === 1 ? recs[0] : null;
  const head = recs[0];
  const initials = (who: string) =>
    who
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join('');
  return (
    <li className={`lchk ${c.severity === 'blocker' ? 'dgr' : 'warn'}`} data-testid={`check-${c.key}`}>
      <span className="lchkw">
        {head ? (
          <span className="av" aria-hidden="true">
            {initials(head.who)}
          </span>
        ) : (
          <span className={`lchki ${c.severity === 'blocker' ? 'dgr' : 'warn'}`}>
            <Icon name={c.severity === 'blocker' ? 'stop' : 'info'} />
          </span>
        )}
        {one ? (
          <span className="lrecs">
            {one.to && (acts || c.to) ? (
              <Link to={link(one.to)}>
                <b>{one.who}</b>
                {one.date && (
                  <>
                    <span className="sr">, </span>
                    <small>{fmtD(one.date)}</small>
                  </>
                )}
                {one.what && <span className="sr">: {one.what}</span>}
              </Link>
            ) : (
              <span>
                <b>{one.who}</b>
                {one.date && (
                  <>
                    <span className="sr">, </span>
                    <small>{fmtD(one.date)}</small>
                  </>
                )}
              </span>
            )}
          </span>
        ) : head ? (
          <span>
            <b>{head.who}</b>
            <small>{T('og {n} til', { n: recs.length - 1 })}</small>
          </span>
        ) : null}
      </span>
      <span className="lchkt">
        <b>{T(c.title)}</b>
        <span>{T(c.detail)}</span>
        {note && <span>{note}</span>}
        {/* every person, day or line the count is made of, each opening where it is fixed (#264) */}
        {recs.length > 1 && (
          <details className="lrecs">
            <summary>{T('Gjelder {n}', { n: recs.length })}</summary>
            <ul aria-label={T('Gjelder')}>{recs.map(rec)}</ul>
          </details>
        )}
      </span>
      <span className="lchka">
        {acts ??
          (c.to ? (
            <Link className="mlink" to={c.to}>
              {T('Åpne')}
              <Icon name="chevronRight" />
            </Link>
          ) : head?.to ? (
            // no list to open: the first record is where it is fixed
            <Link className="mlink" to={link(head.to)}>
              {T('Åpne opplysning')}
              <Icon name="chevronRight" />
            </Link>
          ) : null)}
      </span>
    </li>
  );
}

/** The words of each control status, for the row and the list's status filter (V7.4, #281). */
export const STATE_LABEL: Record<RowState, string> = {
  avklares: 'Må avklares',
  merknad: 'Merknad',
  kontrollert: 'Kontrollert',
  ingen: 'Ingen timer',
};
/** "Kontroll" in a row: what is still in the way for this person's month, from the same checks as above. */
export function RowStatus({ l, state }: { l: PayLine; state: RowState }) {
  const left = l.unapproved + l.open;
  if (state === 'avklares')
    return <Status tone="warn">{left ? T('{n} gjenstår', { n: left }) : T(STATE_LABEL.avklares)}</Status>;
  return <Status tone={state === 'kontrollert' ? 'ok' : 'neu'}>{T(STATE_LABEL[state])}</Status>;
}

/** a shift from the date-range route over every store, which names the store it belongs to */
type StoreShift = ApiShift & { tenant_id?: number | string };

interface SystemFile {
  /** the report route's format= */
  format: string;
  title: string;
  cols: string;
  type: string;
  name: (ym: string) => string;
  done: string;
  failed: string;
}
const TRIPLETEX_FILE: SystemFile = {
  format: 'tripletex',
  title: 'Tripletex CSV',
  cols: 'År, Måned, Ansattnr, Lønnsartnummer, Kommentar, Antall, Sats. En linje per lønnsart.',
  type: 'text/csv;charset=utf-8',
  name: (ym) => `tripletex-${ym}.csv`,
  done: 'Tripletex CSV lastet ned',
  failed: 'Kunne ikke hente Tripletex-eksporten',
};
/** The payroll system's own file (#110), by Innstillinger → Lønn «Lønnssystem». A system without an entry gets Tripletex's. */
const SYSTEM_FILE: Partial<Record<PayrollSystem, SystemFile>> = {
  tripletex: TRIPLETEX_FILE,
  poweroffice: {
    format: 'poweroffice',
    title: 'PowerOffice-fil',
    cols: 'Lønnsgrunnlag for PowerOffice Go (beta), Import, Lønnsgrunnlag. En linje per ansatt, lønnsart og prosjekt.',
    type: 'text/plain;charset=us-ascii',
    name: (ym) => `poweroffice-lonnsgrunnlag-${ym}.hlt`,
    done: 'PowerOffice-fil lastet ned',
    failed: 'Kunne ikke hente PowerOffice-filen',
  },
};

/**
 * The files «Last ned fil» offers (export 3.1, #281): a report for reading first, CSV by default, then the
 * payroll systems' import files. None needs a connection. `label` names the file on the button.
 */
type FileKey = 'csv' | 'xlsx' | 'pdf' | 'tripletex' | 'poweroffice';
const FILES: {
  k: FileKey;
  group: 'report' | 'import';
  title: string;
  text: string;
  ext: string;
  label: string;
}[] = [
  {
    k: 'csv',
    group: 'report',
    title: 'CSV',
    text: 'Åpne i regneark eller del med regnskapsfører.',
    ext: '.csv',
    label: 'CSV-rapport',
  },
  {
    k: 'xlsx',
    group: 'report',
    title: 'Excel',
    text: 'Redigerbar rapport med tall i egne kolonner og en sumlinje.',
    ext: '.xlsx',
    label: 'Excel-rapport',
  },
  {
    k: 'pdf',
    group: 'report',
    title: 'PDF',
    text: 'Lesbar rapport til gjennomgang og arkiv, én tabell per ansatt.',
    ext: '.pdf',
    label: 'PDF-rapport',
  },
  {
    k: 'tripletex',
    group: 'import',
    title: 'Tripletex',
    text: 'Importfil til Tripletex Lønn, én linje per lønnsart.',
    ext: '.csv',
    label: 'Tripletex-fil',
  },
  {
    k: 'poweroffice',
    group: 'import',
    title: 'PowerOffice Go',
    text: 'Lønnsgrunnlag i fast HLT-format. Beta: ikke prøvd mot en ekte import ennå.',
    ext: '.hlt',
    label: 'PowerOffice-fil',
  },
];
const PICK_KEY = 'timefly.lonn.file';
/** the last file chosen, for this tab only; storage can be blocked, then CSV */
const pickedFile = (): FileKey => {
  try {
    const v = sessionStorage.getItem(PICK_KEY);
    return FILES.some((f) => f.k === v) ? (v as FileKey) : 'csv';
  } catch {
    return 'csv';
  }
};

export default function Lonn() {
  const can = useCan();
  const { tenant, tenants } = useTenant();
  const [sp, setSp] = useSearchParams();
  // the list's own search, status and page (V7.4, #281): in the address so Back and a refresh keep them,
  // never part of the report or the export scope; a new search or status starts on the first page
  const setParam = (k: 'sok' | 'status' | 'side', v: string | null) =>
    setSp(
      (n) => {
        if (v) n.set(k, v);
        else n.delete(k);
        if (k !== 'side') n.delete('side');
        return n;
      },
      { replace: true },
    );
  const listQ = sp.get('sok') ?? '';
  const askedState = sp.get('status') ?? '';
  const stateFilter = (ROW_STATES as string[]).includes(askedState) ? (askedState as RowState) : null;
  const askedPage = Number(sp.get('side') ?? 1);
  // ?mnd=2026-09 is the month: the address holds it, so Back from a correction, a refresh or a
  // shared link lands on the month that was being paid (#264)
  const asked = sp.get('mnd');
  const ym = asked && /^\d{4}-\d{2}$/.test(asked) ? asked : monthOf(today());
  const setYm = (v: string) =>
    setSp(
      (n) => {
        n.set('mnd', v);
        return n;
      },
      { replace: true },
    );
  // ?vis=eksport opens the files for the month (the welcome overview's «Send måneden», #130);
  // ?vis=kontroll the checks, kept in the address while open so Back from a record returns to them;
  // ?ansatt= a person's grunnlag, so Back from a correction reopens it (V7.4, #281)
  const askedPerson = sp.get('ansatt') ?? '';
  const [pane, setPane] = useState<Pane>(
    sp.get('vis') === 'eksport'
      ? { k: 'export' }
      : sp.get('vis') === 'historikk'
        ? { k: 'history' }
        : sp.get('vis') === 'lagrede'
          ? { k: 'saved' }
          : /^\d+$/.test(askedPerson)
            ? { k: 'lines', id: Number(askedPerson) }
            : null,
  );
  // the checks are a disclosure on the page (V7.4): closed, with the count on its summary
  const [checksOpen, setChecksOpen] = useState(sp.get('vis') === 'kontroll');
  const vis =
    pane?.k === 'export'
      ? 'eksport'
      : pane?.k === 'history'
        ? 'historikk'
        : pane?.k === 'saved'
          ? 'lagrede'
          : checksOpen
            ? 'kontroll'
            : null;
  const person = pane?.k === 'lines' ? String(pane.id) : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: written when the open pane changes
  useEffect(() => {
    if ((sp.get('vis') ?? null) === vis && (sp.get('ansatt') ?? null) === person) return;
    setSp(
      (n) => {
        if (vis) n.set('vis', vis);
        else n.delete('vis');
        if (person) n.set('ansatt', person);
        else n.delete('ansatt');
        return n;
      },
      { replace: true },
    );
  }, [vis, person]);
  const from = firstOf(ym);
  const to = lastOf(ym);
  // which stores and people (plan 0031); the screen and both files follow the same choice
  const scope = useScope(tenant?.id, tenants);
  const reportQ = useReport(from, to, scope.query);
  // the month's wage cost with feriepenger, pension and AGA, and its share of the month's sales (BK, 2026-10-28, L7b)
  const labour = useLabour();
  const { sales } = useSales(from, to);
  const usersQ = useUsers();
  const shiftsQ = useShifts(from, to);
  const typesQ = useAbsenceTypes();
  const monthQ = usePayMonth(ym);
  const allRows = reportQ.data?.payroll ?? [];
  const rows = scope.pick(allRows);
  // someone who left during the month still has pay in the file, so they stay on the screen and in
  // its checks: without them the screen's sum was lower than the file's (review 2026-10-06)
  const paid = new Set(rows.filter((r) => Object.keys(r.totals ?? {}).length).map((r) => String(r.id)));
  // the report's number is the one the file and the send carry: the Lønns-ID, else Ansattnr (2026-10-16)
  const rowNr = new Map(rows.filter((r) => r.employee_id).map((r) => [String(r.id), String(r.employee_id)]));
  const here = (usersQ.data ?? [])
    .filter((u) => u.active_status || paid.has(String(u.id)))
    .map((u) => ({ ...u, employee_id: rowNr.get(String(u.id)) ?? u.employee_id ?? null }));
  // from another store a person is known by the report row alone (plan 0031)
  const known = new Set(here.map((u) => String(u.id)));
  const users = [
    ...(scope.people ? here.filter((u) => scope.people?.includes(String(u.id))) : here),
    ...rows
      .filter((r) => !known.has(String(r.id)))
      .map((r) => {
        const [name, ...rest] = r.name.split(' ');
        return {
          id: Number(r.id),
          name: name ?? r.name,
          lastname: rest.join(' '),
          employee_id: r.employee_id ?? null,
          active_status: true,
        } as ApiUser;
      }),
  ];
  const shifts = (shiftsQ.data?.shifts ?? []).filter((s) => s.user_id != null);
  const day0 = today();
  // over several stores the hours and the counts read every chosen store's shifts (plan 0031 slice 2)
  const acrossQ = useQuery({
    queryKey: ['shifts', from, to, 'stores'],
    enabled: scope.multi,
    queryFn: () =>
      get<{ shifts: StoreShift[] }>(
        `/admin/shifts/date-range?start_date=${from}&end_date=${to}&include_multi_tenant=true`,
      ),
    staleTime: 30_000,
  });
  const inStore = (id: number) =>
    (acrossQ.data?.shifts ?? []).filter((s) => s.user_id != null && Number(s.tenant_id) === id);
  const others = scope.multi
    ? scope.stores
        .filter((id) => id !== tenant?.id)
        .map((id) => ({
          shifts: inStore(id),
          policy: breakPolicy(tenants.find((t) => t.id === id)),
        }))
    : [];
  // the active store counts only when chosen; its approvals stay on this screen either way
  const ownShifts = scope.multi && tenant && !scope.stores.includes(tenant.id) ? [] : shifts;

  // Every krone from the report the files are written from; the hours from
  // the approved shifts under the company's break policy, null until the
  // company has loaded, each shift clipped to the month as the report clips
  // it; the counts from the period's shifts (lonn/lines.ts).
  // the worked-hours column waits for the other stores' shifts, then sums each under its own break
  const lines = payLines(
    users,
    rows,
    ownShifts,
    day0,
    scope.multi && !acrossQ.data ? null : breakPolicy(tenant),
    { from, to },
    Date.now(),
    others,
  );

  // The checks before the file leaves (lonn/run.ts). What counts as a blocker is read off
  // generateTripletexCSV rather than guessed; see that file's header.
  const payTypesQ = usePayrollTypes();
  const rateRulesQ = useRateRules();
  // the timebank: people who left with hours in it, and the payout date (plan 0027 G2 slice 3)
  const banksQ = useCompanyTimeBank();
  const projectsRequired = useProjectRequired();
  const list = checks({
    users,
    lines,
    rows,
    projectsRequired,
    absenceTypes: typesQ.data ?? [],
    payTypes: payTypesQ.data ?? [],
    rateRules: rateRulesQ.data ?? [],
    banks: Array.isArray(banksQ.data) ? banksQ.data : [],
    unused: {
      rule: tenant?.avspasering_unused,
      day: tenant?.avspasering_payout_day,
    },
    period: { from, to },
    payrollSystem: tenant?.payroll_system ?? 'tripletex',
    // a passed end date with shifts after it (#108): the shifts as Oslo dates, today in Oslo
    // with the clocked hours and the approval, for the long-shift and summed-day checks (#217)
    shifts: shifts.map((s) => ({
      id: s.id,
      user_id: s.user_id,
      start_time: oslo(s.start_time)?.date ?? '',
      hours:
        s.punch_in_time && s.punch_out_time
          ? (Date.parse(s.punch_out_time) - Date.parse(s.punch_in_time)) / 3600_000
          : null,
      approved: !!s.is_approved,
    })),
    today: day0,
  });
  const stepList = steps(list);
  const stopping = blockers(list);
  // #217: these hold «Send» to the payroll system until someone has looked at them this month; the files stay free
  const [seen, setSeen] = useState<string[]>([]);
  const held = list.filter((c) => HOLD_UNTIL_SEEN.includes(c.key) && !seen.includes(`${ym}:${c.key}`));
  // each person's control status from the same checks, and the rows the list shows (V7.4, #281)
  const heldKeys = held.map((c) => c.key);
  const stateOf = (l: PayLine) => rowState(l, checksFor(list, l.u.id), heldKeys);
  const shown = filterRows(lines, { q: listQ, state: stateFilter }, stateOf);
  const pg = pageOf(shown, askedPage);
  const listFiltered = listQ.trim() !== '' || stateFilter != null;
  // Ready is a fact about this month and scope, not the absence of an answer (#262): the strip, the
  // figures and the files wait for every source the checks read, and a failed one is named
  const sources = [
    { what: T('lønnsrapporten'), q: reportQ },
    { what: T('de ansatte'), q: usersQ },
    { what: T('vaktene'), q: shiftsQ },
    { what: T('fraværstypene'), q: typesQ },
    { what: T('lønnsartene'), q: payTypesQ },
    { what: T('satsreglene'), q: rateRulesQ },
    { what: T('timebanken'), q: banksQ },
    ...(scope.multi ? [{ what: T('vaktene i de andre bedriftene'), q: acrossQ }] : []),
  ];
  const ready = readiness(
    sources.map(({ what, q }) => ({ what, ...q })),
    list.length,
    // empty is nobody with pay or hours in the selection, not no rows (#262 L-01)
    lines.filter((l) => l.total !== 0 || (l.approvedH ?? 0) > 0).length,
  );
  // the figures on screen are this month's (stale is the last answer for this same month, being read again)
  const current = ready.state !== 'pending' && ready.state !== 'error';
  const retry = () => {
    for (const { q } of sources) if (q.isError) q.refetch();
  };

  // Approving here (plan 0026 P1): shifts inside the plan's tolerance in one click, by the same
  // rule and the same requests as Timeliste. A deviation needs someone to look at it, and an open
  // punch needs a time, so those go to Timeliste on this month with the filter set.
  const { approve, busy, dialog: absenceDialog } = useApproveMany(shifts);
  const tol = tenantRules().toleranceMin ?? 15;
  const now = Date.now();
  const kinds = shifts
    .filter((s) => {
      const d = oslo(s.start_time)?.date ?? '';
      return d >= from && d <= to;
    })
    .map((s) => ({ id: s.id, k: kindOf(s, now, tol) }));
  const readyIds = kinds.filter((x) => x.k === 'ready').map((x) => x.id);
  const devN = kinds.filter((x) => x.k === 'deviation').length;
  // the other stores' unapproved days are in the count; they are approved in their own store
  const elsewhere = others
    .flatMap((o) => o.shifts)
    .filter((s) => {
      const d = oslo(s.start_time)?.date ?? '';
      const k = kindOf(s, now, tol);
      return d >= from && d <= to && (k === 'ready' || k === 'deviation');
    }).length;
  // every Timeliste destination opens on the month being paid; it and the employee card carry this exact
  // address back (#264): the month, the open checks and the list's search, status and page
  const backHere = (() => {
    const n = new URLSearchParams(sp);
    n.set('mnd', ym);
    return encodeURIComponent(`/lonn?${n.toString()}`);
  })();
  const inMonth = (to: string) => {
    const sep = to.includes('?') ? '&' : '?';
    if (to.startsWith('/timeliste')) return `${to}${sep}mnd=${ym}&fra=lonn&tilbake=${backHere}`;
    if (to.startsWith('/ansatte/')) return `${to}${sep}tilbake=${backHere}`;
    return to;
  };
  const vakter = (n: number) => `${n} ${n === 1 ? T('vakt') : T('vakter')}`;
  const checkRow = (c: Check) =>
    c.key === 'unapproved' ? (
      <CheckRow
        key={c.key}
        c={c}
        link={inMonth}
        note={[
          readyIds.length
            ? T('{n} innenfor planen, kan godkjennes her', {
                n: vakter(readyIds.length),
              })
            : '',
          devN ? T('{n} avviker fra planen og må ses over', { n: vakter(devN) }) : '',
          elsewhere
            ? T('{n} i andre butikker, godkjennes der', {
                n: vakter(elsewhere),
              })
            : '',
        ]
          .filter(Boolean)
          .join('. ')}
        acts={
          <>
            {devN > 0 && (
              <Link className="mlink" to={inMonth('/timeliste?vis=avvik')}>
                {T('Se avvik')}
                <Icon name="chevronRight" />
              </Link>
            )}
            {readyIds.length > 0 && can('timeliste.godkjenne') && (
              <Button tone="pri" small disabled={busy} onClick={() => approve(readyIds)}>
                <Icon name="check" />{' '}
                {busy ? T('Godkjenner…') : T('Godkjenn {n} klare', { n: readyIds.length })}
              </Button>
            )}
            {absenceDialog}
          </>
        }
      />
    ) : c.key === 'open' ? (
      <CheckRow
        key={c.key}
        c={c}
        link={inMonth}
        acts={
          <Link className="mlink" to={inMonth('/timeliste?vis=uten')}>
            {T('Legg inn tider')}
            <Icon name="chevronRight" />
          </Link>
        }
      />
    ) : HOLD_UNTIL_SEEN.includes(c.key) ? (
      <CheckRow
        key={c.key}
        c={c}
        link={inMonth}
        acts={
          seen.includes(`${ym}:${c.key}`) ? (
            <span className="lnote">{T('Sett over')}</span>
          ) : (
            <Button small onClick={() => setSeen((x) => [...x, `${ym}:${c.key}`])}>
              <Icon name="check" /> {T('Jeg har sett over dem')}
            </Button>
          )
        }
      />
    ) : (
      <CheckRow key={c.key} c={c.to ? { ...c, to: inMonth(c.to) } : c} link={inMonth} />
    );

  const tot = lines.reduce((a, l) => ({ h: a.h + (l.approvedH ?? 0), t: a.t + l.total }), { h: 0, t: 0 });
  const rates = ratesOf(labour.rates);
  // a fixed salary is paid in full whatever the hours, in the store that pays it: the month's salary (a share of it for a part
  // of a month), then the supplements and overtime on top; hours times the derived rate put Lura's
  // September at 20 % where payroll paid 33,5 % (Kristina, 2026-10-29)
  const monthShare =
    ((Date.parse(to) - Date.parse(from)) / 864e5 + 1) /
    new Date(Number(from.slice(0, 4)), Number(from.slice(5, 7)), 0).getDate();
  const wages = lines.reduce(
    (a, l) =>
      a +
      (l.monthly && l.u.fixed_salary_here ? l.u.fixed_salary_here * monthShare + l.total - l.base : l.total),
    0,
  );
  const cost = costOf(wages, rates).total;
  // sales are the active store's, for everyone: one store and nobody left out, or the share is wrong
  // a figure under 1 000 kr is a slip, as on Omsetning: two test entries of 1 and 0,50 kr gave
  // BK Bystasjon's September 20 million % (Kristina, 2026-10-29)
  const salesDays = Object.values(sales).filter((v) => salesState(Number(v)) === 'ok');
  const salesSum = salesDays.reduce((a, v) => a + Number(v), 0);
  const daysSoFar =
    from > today()
      ? 0
      : Math.max(0, Math.min(Number(to.slice(8)), to > today() ? Number(today().slice(8)) : 31));
  // The approved cost is the whole month's and has no per-day split, so the share is only true when every
  // day so far has its sales: a registered 0 counts, a missing day or a slip does not (#263). With a day
  // missing the share is shown with its day count, «8 av 29 dager», and the per-day page, which leaves
  // out both sides of that day, is named (owner 2026-10-29: show it with the count, not withheld).
  const covered = Object.entries(sales).filter(
    ([d, v]) => d <= day0 && ['ok', 'zero'].includes(salesState(Number(v))),
  ).length;
  const sharable = !scope.multi && !scope.people && salesSum > 0;
  const pct = sharable && current && !labour.pending && !labour.failed ? (cost / salesSum) * 100 : null;
  const qc = useQueryClient();
  const exported = () => invalidatePayMonth(qc);
  // the hours the page shows (#265): each file is asked for with this revision, and the server refuses
  // it when they changed since; a failed file reads the report again so the page shows what is there now
  const rev = reportQ.data?.revision ? `&revision=${reportQ.data.revision}` : '';
  const reread = () => qc.invalidateQueries({ queryKey: ['report'] });
  /** export=true: the server records the file in the export log before it answers (D23). */
  /** Every file waits for the month's own checks, whatever state the button was drawn in (#262). */
  const unchecked = () => {
    if (settled(ready.state)) return false;
    toast(T('Vent til kontrollen er ferdig.'));
    return true;
  };
  const download = async () => {
    if (unchecked()) return;
    if (!reportQ.data?.csv) return toast(T('Ingen rapport å laste ned'));
    try {
      const { csv } = await get<{ csv: string }>(
        `/admin/report?start_date=${from}&end_date=${to}&export=true${scope.fileQuery}${rev}`,
      );
      if (!csv) return toast(T('Ingen rapport å laste ned'));
      const a = document.createElement('a');
      // the BOM makes Excel read æøå as UTF-8; the Tripletex file already carries its own
      a.href = URL.createObjectURL(new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' }));
      a.download = `lonn-${ym}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast(T('CSV lastet ned'));
      exported();
    } catch (e) {
      toast(`${T('Kunne ikke hente filen')}: ${(e as Error).message}`);
      reread();
    }
  };
  /**
   * The same month, stores and people as Excel or PDF, built by the server from the generic CSV's
   * rows and logged as its own format (#123). Kontroller runs here, so its blockers go along and
   * the file says so on its first line.
   */
  const [fileBusy, setFileBusy] = useState<'xlsx' | 'pdf' | null>(null);
  const downloadFile = async (format: 'xlsx' | 'pdf') => {
    if (unchecked()) return;
    setFileBusy(format);
    const warn = stopping.map((c) => c.title).join('; ');
    try {
      await saveFile(
        `/admin/report?start_date=${from}&end_date=${to}&format=${format}${scope.fileQuery}${rev}${warn ? `&kontroll=${encodeURIComponent(warn)}` : ''}`,
        `lonn-${ym}.${format}`,
      );
      toast(format === 'xlsx' ? T('Excel lastet ned') : T('PDF lastet ned'));
      exported();
    } catch (e) {
      toast(`${T('Kunne ikke hente filen')}: ${(e as Error).message}`);
      reread();
    } finally {
      setFileBusy(null);
    }
  };
  const [triBusy, setTriBusy] = useState(false);
  // the company's payroll system decides which import file is chosen first (#110); one without its own file gets Tripletex's
  const own = SYSTEM_FILE[tenant?.payroll_system ?? 'tripletex'] ?? TRIPLETEX_FILE;
  const [pick, setPickState] = useState<FileKey>(pickedFile);
  const setPick = (k: FileKey) => {
    setPickState(k);
    setWarned(false);
    try {
      sessionStorage.setItem(PICK_KEY, k);
    } catch {
      /* blocked storage: the choice lasts until the page closes */
    }
  };
  const chosen = FILES.find((f) => f.k === pick) ?? (FILES[0] as (typeof FILES)[number]);
  // the import file chosen, else the company's own, for the «already exported» check and the send rows below
  const sys = pick === 'tripletex' || pick === 'poweroffice' ? (SYSTEM_FILE[pick] ?? TRIPLETEX_FILE) : own;
  /**
   * Same route, same start_date and end_date as useReport, plus format=tripletex or poweroffice
   * (admin/shiftController.js getShiftReportByDateRange). The company is the
   * token's active one, decided server-side; no client parameter names it. The
   * file arrives as the csv string (the Tripletex one with its UTF-8 BOM and quoted fields), kept as is.
   */
  // The month already left as this system's file or send (#183): PowerOffice refuses only a byte-identical
  // file, so a corrected one imported on top of the first pays the hours twice (the pilot customer,
  // 2026-10-19). The first click says when and by whom; the second downloads.
  // ponytail: the active store's log only; a file over several stores is checked for this store alone
  const before = (monthQ.data?.exports ?? []).find(
    (x) => x.format === `${sys.format}_csv` || x.format === `${sys.format}_api`,
  );
  const [warned, setWarned] = useState(false);
  const downloadTripletex = async () => {
    if (unchecked()) return;
    if (before && !warned) return setWarned(true);
    setWarned(false);
    setTriBusy(true);
    try {
      const { csv } = await get<{ csv: string }>(
        `/admin/report?start_date=${from}&end_date=${to}&format=${sys.format}${scope.fileQuery}${rev}`,
      );
      if (!csv) return toast(T('Ingen rapport å laste ned'));
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([csv], { type: sys.type }));
      a.download = sys.name(ym);
      a.click();
      URL.revokeObjectURL(a.href);
      toast(T(sys.done));
      exported();
    } catch (e) {
      toast(`${T(sys.failed)}: ${(e as Error).message}`);
      reread();
    } finally {
      setTriBusy(false);
    }
  };

  const fetching = triBusy || !!fileBusy;
  /** «Last ned» in the footer fetches the chosen file through the same functions and checks as before */
  const fetchChosen = () =>
    pick === 'csv'
      ? download()
      : pick === 'xlsx' || pick === 'pdf'
        ? downloadFile(pick)
        : downloadTripletex();

  const month = monthLabel(ym);
  const names = tenants.filter((t) => scope.stores.includes(t.id)).map((t) => t.name);
  const scopeNames = names.length ? names : [tenant?.name ?? ''];
  // present and empty is nobody, on the screen and in the files (backend reads `people=` the same)
  const nobody = scope.people?.length === 0;
  const mayExport = can('timerloenn.sende');
  // the company's payroll system and how the month reaches it (V7.4 delivery row, #281): «koblet til» is
  // what the server has on record, not a tested connection; a reader without the export is not asked
  const sysKey = tenant?.payroll_system ?? 'tripletex';
  const sysName = SYSTEM_NAME[sysKey] ?? sysKey;
  const connected = useConnected(
    sysKey === 'poweroffice' ? POWEROFFICE : TRIPLETEX,
    mayExport && sysKey !== 'visma',
  );
  const method =
    sysKey !== 'visma' && connected
      ? T('{name}, koblet til: direkte sending eller fil', { name: sysName })
      : T('{name}, filimport', { name: sysName });
  const parts = lines.reduce(
    (a, l) => ({ b: a.b + l.base, s: a.s + l.supp, o: a.o + l.ot, f: a.f + l.absence }),
    { b: 0, s: 0, o: 0, f: 0 },
  );
  const selected = lines.find((l) => pane?.k === 'lines' && l.u.id === pane.id);
  const toggle = (p: NonNullable<Pane>) =>
    setPane((x) => (x && x.k === p.k && (x.k !== 'lines' || x.id === (p as { id: number }).id) ? null : p));
  // over several stores the push is one per Tripletex company (plan 0031 slice 2); a subset of
  // people still goes by file, since a push is every employee of the company
  const tripletexNote = held.length
    ? T('Sending venter til du har sett over: {checks}. Se Kontroller.', {
        checks: held.map((c) => c.title.toLowerCase()).join(', '),
      })
    : scope.people
      ? T(
          'Direkte sending gjelder alle ansatte i bedriften. Velg Alle ansatte, eller bruk filen for dette utvalget.',
        )
      : null;

  const panel = (() => {
    if (!pane) return null;
    const close = () => setPane(null);
    if (pane.k === 'checks') return null;
    if (pane.k === 'lines' && selected) {
      // «Se grunnlag» (V7.4, #281): the same facts for everyone, then this person's checks, each with the way
      // to the exact record; the correction is made where the record lives, and approving stays its own step
      const mine = checksFor(list, selected.u.id);
      const fix = mine.flatMap((c) => c.records ?? []).find((r) => uidOf(r) === selected.u.id && r.to);
      return (
        <DetailPanel
          title={<PersonLabel id={selected.u.id} name={fullName(selected.u)} />}
          sub={`${T('Lønnsgrunnlag')}, ${month}`}
          onClose={close}
          label={T('Grunnlag for {name}', { name: fullName(selected.u) })}
          restoreFocusTo={`button[data-person="${selected.u.id}"]`}
          footer={
            <>
              <Button onClick={close}>{T('Lukk')}</Button>
              <Link className="btn" to={inMonth(`/timeliste?ansatt=${selected.u.id}&vis=alle`)}>
                <Icon name="clock" />
                {T('Se registreringer')}
              </Link>
              {fix?.to && (
                <Link className="btn pri" to={inMonth(fix.to)} data-testid="facts-fix">
                  {T('Åpne kontrollpunkt')}
                  <Icon name="chevronRight" />
                </Link>
              )}
            </>
          }
        >
          <div className="lfacts" data-testid="facts">
            <Fact k={T('Bedrift')}>{scopeNames.join(', ')}</Fact>
            <Fact k={T('Ansattnummer')}>
              {selected.u.employee_id ? (
                selected.u.employee_id
              ) : (
                <Link className="mlink" to={`/ansatte/${selected.u.id}?rediger=employee_id`}>
                  {T('Mangler')}
                </Link>
              )}
            </Fact>
            <Fact k={T('Kontroll')}>
              <RowStatus l={selected} state={stateOf(selected)} />
            </Fact>
          </div>
          {mine.map((c) => {
            const r = c.records?.find((x) => uidOf(x) === selected.u.id);
            return (
              <Notice
                key={c.key}
                tone={c.severity === 'blocker' || heldKeys.includes(c.key) ? 'warn' : 'info'}
                title={T(c.title)}
              >
                {r?.date && `${fmtD(r.date)}. `}
                {r?.what && `${r.what}. `}
                {T(c.detail)}
              </Notice>
            );
          })}
          {mine.length > 0 && (
            <p className="lnote">{T('Retting og godkjenning er to separate handlinger.')}</p>
          )}
          <PayDetail l={selected} />
          <Link className="mlink" to={`/ansatte/${selected.u.id}`}>
            <Icon name="user" />
            {T('Åpne ansattkort')}
          </Link>
        </DetailPanel>
      );
    }
    if (pane.k === 'export')
      return (
        <DetailPanel
          title={T('Last ned lønnsgrunnlag')}
          sub={T('Velg filen som passer oppgaven din.')}
          onClose={close}
          label={T('Last ned lønnsgrunnlag')}
          footer={
            <>
              <span className="lfpick" data-testid="file-chosen">
                {T('Valgt')}: <b>{T(chosen.label)}</b>
              </span>
              <Button onClick={close}>{T('Avbryt')}</Button>
              {mayExport ? (
                <Button
                  tone="pri"
                  onClick={fetchChosen}
                  disabled={fetching || nobody || !settled(ready.state)}
                  data-testid="file-download"
                >
                  <Icon name="download" />
                  {fetching
                    ? T('Henter…')
                    : warned
                      ? T('Last ned likevel')
                      : T('Last ned {file}', { file: T(chosen.label) })}
                </Button>
              ) : (
                <Chip tone="neu" small>
                  {T('krever tillatelse')}
                </Chip>
              )}
            </>
          }
        >
          {!settled(ready.state) && (
            <Notice tone="warn" title={T('Kontrollen er ikke ferdig')}>
              {ready.state === 'error'
                ? T('Mangler {what}. Filene kan lastes ned når alt er hentet.', {
                    what: ready.failed.join(', '),
                  })
                : T('Filene kan lastes ned når alt for {month} er hentet.', { month })}
            </Notice>
          )}
          {nobody ? (
            <Notice tone="dgr" title={T('Ingen ansatte er valgt.')}>
              {T('Filen ville vært tom. Velg ansatte før du eksporterer.')}
            </Notice>
          ) : (
            stopping.length > 0 && (
              <Notice
                tone="warn"
                title={T('Filen blir ufullstendig hvis du laster ned nå.')}
                action={
                  <Button
                    small
                    onClick={() => {
                      setPane(null);
                      setChecksOpen(true);
                      requestAnimationFrame(() =>
                        document.getElementById('kontrollpunkter')?.scrollIntoView?.({ block: 'start' }),
                      );
                    }}
                  >
                    <Icon name="list" />
                    {T('Se kontrollpunkter')}
                  </Button>
                }
              >
                {stopping.map((c) => c.title.toLowerCase()).join(', ')}. {T('Du kan laste ned likevel.')}
              </Notice>
            )
          )}
          {/* the preview names exactly what the files carry (V7.4, #265): the list's search is not in it */}
          <div className="lfacts lprev" data-testid="export-period">
            <Fact k={T('Bedrift')}>{scopeNames.join(', ')}</Fact>
            <Fact k={T('Periode')}>
              {month}, {fmtD(from)} til {fmtD(to)}
            </Fact>
            <Fact k={T('Utvalg')}>
              {scope.people
                ? T('{n} ansatte valgt', { n: scope.people.length })
                : T('Alle ansatte med lønn, {n}', { n: lines.length })}
            </Fact>
            <Fact k={T('Godkjente timer')}>{settled(ready.state) ? `${num(tot.h)} t` : '–'}</Fact>
            <Fact k={T('Beløp i anslag')}>
              {settled(ready.state) ? `${kr(tot.t)} kr` : '–'}
              {settled(ready.state) && (
                <small>
                  {T('Grunnlønn {b}, tillegg {s}, overtid {o}, fravær {f} kr', {
                    b: kr(parts.b),
                    s: kr(parts.s),
                    o: kr(parts.o),
                    f: kr(parts.f),
                  })}
                </small>
              )}
            </Fact>
            <Fact k={T('Mottaker')}>{mayExport ? method : sysName}</Fact>
            <Fact k={T('Kildeversjon')}>
              <span data-testid="export-revision">{reportQ.data?.revision?.slice(0, 7) ?? '–'}</span>
            </Fact>
          </div>
          {/* download first (export 3.1, #281): one choice, CSV unless the user chose another this session; none needs a connection */}
          {(['report', 'import'] as const).map((g) => (
            <fieldset key={g} className="lfmt" data-testid={`export-${g}`}>
              <legend>
                <b>
                  {g === 'report' ? T('Rapport til deg eller regnskapsfører') : T('Fil til lønnssystemet')}
                </b>
                <span>
                  {g === 'report'
                    ? T('Disse filene krever ingen tilkobling.')
                    : T('Importeres manuelt. Du trenger ikke koble til systemet.')}
                </span>
              </legend>
              {FILES.filter((f) => f.group === g).map((f) => (
                <OptionRow
                  key={f.k}
                  name="lonn-file"
                  value={f.k}
                  checked={pick === f.k}
                  onChange={() => setPick(f.k)}
                  title={T(f.title)}
                  aside={<span className="lfext">{f.ext}</span>}
                  testid={`file-${f.k}`}
                >
                  <span>{T(f.text)}</span>
                </OptionRow>
              ))}
            </fieldset>
          ))}
          <p className="lnote">
            {T(
              'Rapporten er for gjennomgang. CSV, Excel og PDF er ikke en ferdig importfil for alle lønnssystemer.',
            )}{' '}
            {T('Filene gjelder godkjente vakter med stempling og innvilget fravær i dette utvalget.')}{' '}
            {T(
              'Søket i listen endrer ikke utvalget. Før filen lages, sjekker Timefly at timene fortsatt har denne kildeversjonen, og stopper hvis de er endret.',
            )}
          </p>
          {warned && before && (
            <Notice tone="warn" title={T('{month} er allerede eksportert', { month })}>
              <span data-testid="export-again">
                {T(
                  '{file} for {month} ble laget {at} av {who}. Er den importert i lønnssystemet, må du fjerne eller reversere den importen før du importerer en ny fil. Ellers blir timene betalt to ganger.',
                  { file: T(sys.title), month, at: when(before.exported_at), who: before.exported_by_name },
                )}
              </span>
            </Notice>
          )}
          {own.format === 'poweroffice' && (
            <PowerOfficeSend
              key={from}
              from={from}
              to={to}
              allowed={mayExport}
              scopeNote={
                scope.multi && !held.length
                  ? T('Send til PowerOffice gjelder én butikk om gangen. Velg butikken og send derfra.')
                  : tripletexNote
              }
            />
          )}
          {own.format === 'tripletex' && (
            <TripletexSend
              key={`${from}|${scope.query}`}
              from={from}
              to={to}
              allowed={mayExport}
              scopeNote={tripletexNote}
              stores={scope.multi ? scope.stores : null}
            />
          )}
          <Explain q={T('Hva står i filene?')}>
            <p>
              <b>{T('Tripletex CSV')}</b>:{' '}
              {T(
                'kolonnene År, Måned, Ansattnr, Lønnsartnummer, Kommentar, Antall, Sats, Prosjektnummer, Avdelingsnummer, én linje per ansatt og lønnsart. Ansattnr er ansattnummeret; mangler det, står vår id der.',
              )}
            </p>
            {own.format === 'poweroffice' && (
              <p>
                <b>{T('PowerOffice Go')}</b>:{' '}
                {T(
                  'Huldt & Lillevik fast lengde, som PowerOffice Go leser under Import, Lønnsgrunnlag: ansattnummer, lønnsart, prosjektnummer, månedens siste dato, antall og sats, én linje per ansatt, lønnsart og prosjekt. Bare tall, så ansattnummer og lønnsart må være tall. Beta: ikke prøvd mot en ekte import ennå.',
                )}
              </p>
            )}
            <p>
              <b>{T('CSV-rapport')}</b>:{' '}
              {T(
                'kolonnene userId, name, pay_type, hours, rate_type, rate_value, rate, salary_total, med en TOTAL-linje per ansatt.',
              )}
            </p>
            <p>
              <b>{T('Excel og PDF')}</b>:{' '}
              {T(
                'de samme linjene som CSV-rapporten. Excel har tall som tall og en sumlinje nederst; PDF-en har bedriften og perioden øverst og én tabell per ansatt med sum. Stopper Kontroller noe, står det på første linje i filen.',
              )}
            </p>
            <p>{T('Hver fil som lastes ned eller sendes, står i eksporthistorikken med hvem og når.')}</p>
          </Explain>
        </DetailPanel>
      );
    if (pane.k === 'history')
      return (
        <DetailPanel
          title={T('Eksporthistorikk')}
          sub={`${tenant?.name ?? ''}, ${month}`}
          onClose={close}
          label={T('Eksporthistorikk')}
        >
          <MonthHistory ym={ym} />
        </DetailPanel>
      );
    if (pane.k === 'saved')
      return (
        <DetailPanel
          title={T('Lagrede månedsrapporter')}
          sub={`${tenant?.name ?? ''}, ${T('alle måneder')}`}
          onClose={close}
          label={T('Lagrede månedsrapporter')}
        >
          <SavedReports />
        </DetailPanel>
      );
    if (pane.k === 'stores')
      return (
        <DetailPanel
          title={T('Velg bedrifter')}
          sub={T('Samme utvalg brukes på skjermen og i filene.')}
          onClose={close}
        >
          <StoresPick scope={scope} tenants={tenants} />
        </DetailPanel>
      );
    if (pane.k === 'people')
      return (
        <DetailPanel
          title={T('Velg ansatte')}
          sub={T('Ingen valgt betyr ingen ansatte i tabellen og i filene.')}
          onClose={close}
        >
          <PeoplePick
            scope={scope}
            rows={allRows}
            nameOf={(id) => {
              const u = (usersQ.data ?? []).find((x) => String(x.id) === id);
              return u ? fullName(u) : undefined;
            }}
          />
        </DetailPanel>
      );
    return null;
  })();

  return (
    <div className="lonn">
      <LonnHead
        tab="grunnlag"
        action={
          <>
            <Button ghost onClick={() => toggle({ k: 'history' })} aria-expanded={pane?.k === 'history'}>
              <Icon name="clock" />
              {T('Historikk')}
            </Button>
            <Button ghost onClick={() => toggle({ k: 'saved' })} aria-expanded={pane?.k === 'saved'}>
              <Icon name="document" />
              {T('Lagrede rapporter')}
            </Button>
            {/* quiet until the month is ready (V4/V6); it still opens, the files keep their own gate */}
            <Button
              {...(ready.state === 'ready' ? { tone: 'pri' as const } : {})}
              onClick={() => toggle({ k: 'export' })}
              aria-expanded={pane?.k === 'export'}
            >
              <Icon name="download" />
              {T('Last ned fil')}
            </Button>
          </>
        }
      />
      <TabPanel tab="grunnlag">
        <div className="mbar">
          <div className="mf">
            <span className="tllbl">{T('Måned')}</span>
            {/* the app's own words, not the browser's: a native month input follows navigator.language (#89) */}
            <PeriodControl
              label={T('Måned')}
              month
              value={ym}
              onChange={setYm}
              prevLabel={T('Forrige måned')}
              nextLabel={T('Neste måned')}
              onPrev={() => setYm(shiftMonth(ym, -1))}
              onNext={() => setYm(shiftMonth(ym, 1))}
            >
              <span data-testid="month">{month}</span>
            </PeriodControl>
          </div>
          <span className="sp" />
          {tenants.length > 1 && (
            <Button
              onClick={() => toggle({ k: 'stores' })}
              aria-expanded={pane?.k === 'stores'}
              aria-label={`${T('Velg bedrifter')}: ${scopeNames.join(', ')}`}
              data-testid="scope"
            >
              {scope.stores.length > 1
                ? T('{n} bedrifter', { n: scope.stores.length })
                : scopeNames.join(', ')}
            </Button>
          )}
          <Button onClick={() => toggle({ k: 'people' })} aria-expanded={pane?.k === 'people'}>
            {scope.people == null
              ? T('Alle ansatte')
              : nobody
                ? T('Ingen ansatte valgt')
                : T('{n} ansatte valgt', { n: scope.people.length })}
          </Button>
        </div>
        {nobody && (
          <Notice tone="warn" title={T('Ingen ansatte er valgt.')}>
            {T('Tabellen og filene er tomme til du velger ansatte.')}
          </Notice>
        )}
        {/* V7.4 (#281): statement, then the delivery row, then the checks as one disclosure, then the ledger */}
        <div className="lready" data-testid="readiness" data-state={ready.state}>
          {ready.state === 'empty' ? (
            <Notice tone="info" title={T('Ingenting å eksportere for {month}', { month })}>
              {T('Ingen ansatte i utvalget har godkjente timer eller fravær med lønn.')}{' '}
              <Link className="mlink" to={`/timeliste?mnd=${ym}`}>
                {T('Se registreringer')}
                <Icon name="chevronRight" />
              </Link>
            </Notice>
          ) : (
            // the approved statement (V5 to V7.4, #262): amount, hours and people as one hierarchy; unknown
            // figures stay dashes, never zero
            <section className="lstmt" aria-label={T('Lønnsgrunnlag for {month}', { month })}>
              <div className="lsum">
                <span className="llbl">
                  {ready.state === 'error' || ready.state === 'stale'
                    ? T('Lønnsgrunnlag ikke bekreftet')
                    : T('Godkjent lønnsgrunnlag')}
                </span>
                <b className={`lamt${ready.state === 'stale' ? ' old' : ''}`} data-testid="lonn-amount">
                  {current ? kr(tot.t) : '–'} kr
                </b>
                <span className="lsumsub">
                  {T('Anslag av grunnlønn, tillegg, overtid og fravær. Se beregningen.')}
                </span>
              </div>
              <dl className="lfig">
                <div>
                  <dt>{T('Godkjente timer')}</dt>
                  <dd>{current && lines.some((l) => l.approvedH != null) ? num(tot.h) : '–'} t</dd>
                </div>
                <div>
                  <dt>{T('Ansatte')}</dt>
                  <dd>
                    {current ? (
                      <a href="#lonn-ledger" className="lppl">
                        {lines.length}
                      </a>
                    ) : (
                      '–'
                    )}
                  </dd>
                </div>
              </dl>
              <Explain q={T('Beregning')} className="lcalc">
                <ol>
                  {CALC.map((x) => (
                    <li key={x}>{T(x)}</li>
                  ))}
                </ol>
              </Explain>
              {!mayExport && (
                <p className="lro" data-testid="lonn-readonly">
                  {T(
                    'Du har lesetilgang. Du kan se lønnsgrunnlaget og kontrollpunktene, men ikke eksportere.',
                  )}
                </p>
              )}
              {ready.state === 'error' ? (
                <div className="lcue dgr">
                  <p>
                    <b>{T('Kunne ikke kontrollere {month}', { month })}</b>{' '}
                    {T('Mangler {what}. Ingenting er klart før alt er hentet.', {
                      what: ready.failed.join(', '),
                    })}
                  </p>
                  <Button onClick={retry}>
                    <Icon name="refresh" />
                    {T('Prøv igjen')}
                  </Button>
                </div>
              ) : ready.state === 'pending' || ready.state === 'stale' ? (
                <div className="lcue">
                  <output aria-busy="true">
                    <b>
                      {ready.state === 'pending'
                        ? T('Henter lønnsgrunnlaget for {month}', { month })
                        : T('Oppdaterer kontrollen for {month}', { month })}
                    </b>{' '}
                    {ready.state === 'pending'
                      ? T('Kontrollen vises når rapporten, de ansatte og vaktene er hentet.')
                      : T('Tallene er fra forrige henting og oppdateres nå.')}
                  </output>
                </div>
              ) : null}
            </section>
          )}
          <Delivery
            ym={ym}
            system={sysKey}
            connected={connected}
            setupTo={can('innstillinger.se') ? '/innstillinger/lonn#integrasjon' : null}
            onEvent={() => setPane({ k: 'history' })}
            company={scope.multi ? tenant?.name : undefined}
          />
          {monthQ.data?.changed_since_export && (
            <Notice
              tone="warn"
              title={T('Grunnlaget er endret etter eksport')}
              action={
                <button type="button" className="mlink" onClick={() => setPane({ k: 'history' })}>
                  {T('Se hendelse')}
                </button>
              }
            >
              <span data-testid="changed-since-export">
                {T(
                  'Timene er endret etter siste eksport. Filen lønnssystemet har fått stemmer ikke lenger; se over endringene, last ned eller send på nytt, og si fra til den som kjører lønnen.',
                )}{' '}
                <ChangedPeople s={monthQ.data} ym={ym} />
              </span>
            </Notice>
          )}
          {ready.state !== 'empty' &&
            (!current ? (
              <p className="lchkq" id="kontrollpunkter">
                {T('Kontrollpunktene vises når alt for måneden er hentet.')}
              </p>
            ) : list.length === 0 ? (
              // all clear only once this answer is final; while it is read again the statement says so
              ready.state === 'ready' && (
                <p className="lok" id="kontrollpunkter">
                  <Icon name="check" /> <b>{T('Klar til eksport')}</b>{' '}
                  {T('Alle kontrollpunkter er i orden for utvalget.')}
                </p>
              )
            ) : (
              // the named checks as one disclosure; the count stays on its summary when it is closed (V7.4)
              <details
                className={`lchecks ${stopping.length || held.length ? 'warn' : 'info'}`}
                id="kontrollpunkter"
                open={checksOpen}
                onToggle={(e) => setChecksOpen(e.currentTarget.open)}
              >
                <summary data-testid="checks-summary">
                  <Icon name={stopping.length || held.length ? 'warn' : 'info'} />
                  <span className="lchkst">
                    <b id="lchecks-h" className="lcount">
                      {list.length === 1
                        ? T('1 kontrollpunkt før eksport')
                        : T('{n} kontrollpunkter før eksport', { n: list.length })}
                    </b>
                    <span>
                      {stopping.length
                        ? T('{a} stopper eksporten, {b} til merknad.', {
                            a: stopping.length,
                            b: list.length - stopping.length,
                          })
                        : T('Merknader som forklarer linjer. De stopper ikke filen.')}
                    </span>
                  </span>
                  <span className="lchksv" aria-hidden="true">
                    {checksOpen ? T('Skjul') : T('Vis')}
                    <Icon name="chevronDown" />
                  </span>
                </summary>
                <div className="lchksb">
                  {stepList
                    .filter((st) => st.key !== 'eksporter' && list.some((c) => c.step === st.key))
                    .map((st) => (
                      <section key={st.key} className="lchks" data-testid={`step-${st.key}`}>
                        <h3 className="sr">
                          {T(st.title)}{' '}
                          <small>
                            {st.blockers
                              ? T('{n} stopper', { n: st.blockers })
                              : T('{n} å merke seg', { n: st.notes })}
                          </small>
                        </h3>
                        <ul>{list.filter((c) => c.step === st.key).map(checkRow)}</ul>
                      </section>
                    ))}
                  <div className="lchksf">
                    <p className="lhelp">
                      <Icon name="info" />
                      {T('Retting og godkjenning er to separate handlinger.')}
                    </p>
                    <Link className="mlink" to={`/timeliste?mnd=${ym}`}>
                      {T('Gå til gjennomgang')}
                      <Icon name="chevronRight" />
                    </Link>
                  </div>
                  <Explain q={T('Hva bør jeg kontrollere før eksport?')}>
                    <p>
                      {T(
                        'Det som stopper, er det lønnssystemet ikke kan plassere eller ikke får med seg: ansatte uten ansattnummer eller timelønn, linjer uten lønnsart, åpne stemplinger og dager som ikke er godkjent. Bare godkjente dager med stempling inn og ut kommer med.',
                      )}
                    </p>
                    <p>
                      {T(
                        'Merknadene stopper ikke filen, men forklarer en linje: sykedager, NAV-dager, timebank og fravær samme dag som arbeid. Du kan laste ned likevel; filen blir da ufullstendig, og det står hva som mangler.',
                      )}
                    </p>
                  </Explain>
                </div>
              </details>
            ))}
        </div>
        <div className={`msplit${panel ? ' open' : ''}`}>
          <div className="lmain">
            <section
              className={`mcard lledger${ready.state === 'empty' ? ' lnone' : ''}`}
              id="lonn-ledger"
              aria-labelledby="lonn-ledger-h"
            >
              <div className="lsech">
                <div>
                  <h2 id="lonn-ledger-h">
                    {T('Ansatte i lønnsgrunnlaget')} {current && <span className="lcnt">{lines.length}</span>}
                  </h2>
                  <div className="sub" data-testid="month-scope">
                    {scope.multi
                      ? `${T('Godkjente timer')}, ${T('samlet for')} ${scopeNames.join(', ')}, ${month}`
                      : `${T('Godkjente timer')}, ${scopeNames.join(', ')}, ${month}`}
                  </div>
                </div>
              </div>
              {current && lines.length > 0 && (
                // the list's own filters (V7.4): they change the rows, never the totals or the export
                <div className="ltools">
                  <label className="lsrch">
                    <span className="sr">{T('Søk ansatt')}</span>
                    <Icon name="search" />
                    <input
                      className="inp"
                      type="search"
                      value={listQ}
                      onChange={(e) => setParam('sok', e.target.value)}
                      placeholder={T('Søk ansatt')}
                      autoComplete="off"
                      aria-describedby="lonn-listnote"
                      data-testid="lonn-sok"
                    />
                  </label>
                  <select
                    className="inp"
                    aria-label={T('Kontrollstatus')}
                    aria-describedby="lonn-listnote"
                    value={stateFilter ?? ''}
                    onChange={(e) => setParam('status', e.target.value || null)}
                    data-testid="lonn-status"
                  >
                    <option value="">{T('Alle statuser')}</option>
                    {ROW_STATES.map((k) => (
                      <option key={k} value={k}>
                        {T(STATE_LABEL[k])}
                      </option>
                    ))}
                  </select>
                  <p className="lscopenote" id="lonn-listnote">
                    {T('Søket filtrerer listen, ikke eksportutvalget.')}
                  </p>
                </div>
              )}
              <table className="mt lcards" data-testid="lonn">
                <caption className="sr">
                  {T('Ansatte i lønnsgrunnlaget')}, {month}
                </caption>
                <thead>
                  <tr>
                    <th>{T('Ansatt')}</th>
                    <th className="num">{T('Godkjent')}</th>
                    <th className="num">{T('Grunnlag')}</th>
                    <th>{T('Kontroll')}</th>
                    <th>
                      <span className="sr">{T('Detaljer')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(current ? pg.rows : []).map((l) => (
                    <tr
                      key={l.u.id}
                      className={`row${pane?.k === 'lines' && pane.id === l.u.id ? ' sel' : ''}`}
                    >
                      <td>
                        <PersonLabel id={l.u.id} name={fullName(l.u)}>
                          <Link to={`/ansatte/${l.u.id}`} className="lwho">
                            {fullName(l.u)}
                          </Link>
                          <small>
                            {[
                              l.u.employee_id ? `${T('Ansattnr')} ${l.u.employee_id}` : T('Ansattnr mangler'),
                              l.monthly && T('Fastlønn'),
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </small>
                        </PersonLabel>
                      </td>
                      <td className="num" data-k={T('Godkjent')}>
                        {l.approvedH == null ? '–' : `${num(l.approvedH)} t`}
                        {l.monthly && <small>{T('registrert')}</small>}
                      </td>
                      <td className="num" data-k={T('Grunnlag')}>
                        {kr(l.total)} kr
                        {l.monthly && <small>{T('Fastlønn betales i lønnssystemet')}</small>}
                      </td>
                      <td data-k={T('Kontroll')}>
                        <RowStatus l={l} state={stateOf(l)} />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="mlink"
                          data-person={l.u.id}
                          onClick={() => toggle({ k: 'lines', id: l.u.id })}
                          aria-expanded={pane?.k === 'lines' && pane.id === l.u.id}
                          aria-label={T('Se grunnlag for {name}', {
                            name: fullName(l.u),
                          })}
                        >
                          {T('Se grunnlag')}
                          <Icon name="chevronRight" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {current && lines.length > 0 && shown.length === 0 && (
                <div className="lnomatch" data-testid="lonn-nomatch">
                  <output>
                    <b>{T('Ingen ansatte passer søket.')}</b>{' '}
                    {T('Summene og eksporten gjelder fortsatt alle {n} ansatte i utvalget.', {
                      n: lines.length,
                    })}
                  </output>
                  <Button
                    onClick={() =>
                      setSp(
                        (n) => {
                          n.delete('sok');
                          n.delete('status');
                          n.delete('side');
                          return n;
                        },
                        { replace: true },
                      )
                    }
                  >
                    {T('Nullstill søket')}
                  </Button>
                </div>
              )}
              {current && shown.length > 0 && (listFiltered || pg.pages > 1) && (
                <nav className="lpager" aria-label={T('Sider i listen')}>
                  <output data-testid="lonn-range">
                    {listFiltered
                      ? T('{a} til {b} av {n} treff, {all} ansatte i alt', {
                          a: pg.first,
                          b: pg.last,
                          n: shown.length,
                          all: lines.length,
                        })
                      : T('{a} til {b} av {n} ansatte', { a: pg.first, b: pg.last, n: shown.length })}
                  </output>
                  {pg.pages > 1 && (
                    <span className="lpagerb">
                      <Button
                        onClick={() => setParam('side', String(pg.page - 1))}
                        disabled={pg.page <= 1}
                        aria-label={T('Forrige side')}
                      >
                        <Icon name="chevronLeft" />
                      </Button>
                      <span>{T('Side {p} av {n}', { p: pg.page, n: pg.pages })}</span>
                      <Button
                        onClick={() => setParam('side', String(pg.page + 1))}
                        disabled={pg.page >= pg.pages}
                        aria-label={T('Neste side')}
                      >
                        <Icon name="chevronRight" />
                      </Button>
                    </span>
                  )}
                </nav>
              )}
              {ready.state === 'pending' && <Skeleton />}
              {current && !lines.length && (
                <p className="lempty">
                  {nobody
                    ? T('Ingen ansatte er valgt.')
                    : T('Ingen ansatte med registreringer i dette utvalget.')}
                </p>
              )}
              <div className="mcf" data-testid="lonn-total">
                <b>
                  {current && lines.some((l) => l.approvedH != null) ? num(tot.h) : '–'} t {T('godkjent')}
                </b>
                <span className="sp" />
                <b>
                  {current ? kr(tot.t) : '–'} kr {T('i anslag')}
                </b>
              </div>
              <div
                className="mcf"
                data-testid="lonn-cost"
                title={T('Feriepenger {h} %, pensjon {p} %, arbeidsgiveravgift {a} %', {
                  h: num(rates.holidayPct),
                  p: num(rates.pensionPct),
                  a: num(rates.agaPct),
                })}
              >
                <span>
                  {T('Lønnskostnad inkl. feriepenger {h} %, pensjon {p} % og AGA {a} %', {
                    h: num(rates.holidayPct),
                    p: num(rates.pensionPct),
                    a: num(rates.agaPct),
                  })}
                </span>
                <span className="sp" />
                <b>{current && !labour.pending && !labour.failed ? kr(cost) : '–'} kr</b>
                {pct != null && (
                  <b data-testid="lonn-pct">
                    {T('Lønn %')} {num(Math.round(pct * 10) / 10)} %
                  </b>
                )}
                {/* with no sales at all there is no share, but the count still says why (owner 2026-10-29) */}
                {!scope.multi && !scope.people && current && daysSoFar > 0 && covered < daysSoFar && (
                  <span className="sub" style={{ color: 'var(--warn-i)' }} data-testid="lonn-pct-days">
                    {T('{a} av {b} dager med omsetning', { a: covered, b: daysSoFar })}{' '}
                    <Link className="mlink" to="/omsetning">
                      {T('Se lønnsprosent per dag')}
                    </Link>
                  </span>
                )}
              </div>
              <MonthLock ym={ym} to={to} scopeNames={scopeNames} />
            </section>
            <div className="lfoot">
              <p>{T('Beløpene er et anslag. Skatt, feriepenger og utbetaling håndteres i lønnssystemet.')}</p>
              <span className="sp" />
              <Link className="mlink" to={`/arbeidstid?vis=oversikt&periode=maned&mnd=${ym}`}>
                <Icon name="clock" />
                {T('Arbeidstidsoversikt')}
              </Link>
              <Link className="mlink" to={`/timeliste?mnd=${ym}`}>
                {T('Se registreringer')}
                <Icon name="chevronRight" />
              </Link>
            </div>
            {/* D17 B (owner, 2026-09-22): shown for a month after the change, so nobody reading last
              month's file beside this one thinks the tillegg dropped */}
            {Date.now() < D17_NOTICE_UNTIL && (
              <p className="mfoot" data-testid="d17-notice">
                {T(
                  'Nytt fra 22.9.2026: alle timer står på grunnlønn, og tillegg og overtid står som tillegget alene, slik Tripletex fører dem. Summen er den samme; tidligere sto tilleggstimene med full sats og ikke på grunnlønn.',
                )}
              </p>
            )}
          </div>
          {panel}
        </div>
      </TabPanel>
    </div>
  );
}
